/**
 * High-performance economic calendar store with batched updates
 */

import { create } from "zustand";
import { getEconomicCalendarSyncYears } from "../sync-years";
import {
  getEconomicCalendarSyncMonthKeys,
  monthKeyFromEventDate,
} from "../sync-months";
import {
  loadMonthVersions,
  mergeMonthVersions,
  saveMonthVersions,
} from "../month-version-storage";
import {
  loadEconomicCalendarEventsIdb,
  saveEconomicCalendarEventsIdb,
} from "../idb";
import { ECONOMIC_CALENDAR_STORAGE_KEYS } from "@/lib/localStorageKeys/economic-calendar";
import type {
  EconomicEvent,
  EconomicCalendarState,
  EconomicCalendarActions,
  StoredEconomicEvent,
  VybeSyncEconomicCalendarRequest,
} from "../types";

interface EconomicCalendarStore
  extends EconomicCalendarState,
    EconomicCalendarActions {}

const initialState: EconomicCalendarState = {
  byId: new Map(),
  byDate: new Map(),
  byCurrency: new Map(),
  byImpact: new Map(),
  loadingStatus: "idle",
  errorMessage: null,
  calendarSyncTargetYears: [],
  loadedCalendarYears: [],
  allEvents: [],
  currencies: [],
  dateRange: null,
  syncLoaded: false,
  monthVersions: {},
};

function eventMapKey(event: EconomicEvent): string {
  if (event.id) return event.id;
  return `${event.dateTimeISO}-${event.name}-${event.currency}`;
}

function dayKeyFromEvent(event: EconomicEvent): string {
  const raw = event.date;
  return raw.includes("T") ? raw.split("T")[0]! : raw.slice(0, 10);
}

function normalizeImpact(impact: string): "Low" | "Medium" | "High" {
  if (impact === "High" || impact === "Medium") return impact;
  return "Low";
}

function rebuildIndexes(byId: Map<string, EconomicEvent>) {
  const byDate = new Map<string, EconomicEvent[]>();
  const byCurrency = new Map<string, EconomicEvent[]>();
  const byImpact = new Map<"Low" | "Medium" | "High", EconomicEvent[]>();

  const allEvents = Array.from(byId.values())
    .filter((e) => !e.isDeleted)
    .sort(
      (a, b) =>
        new Date(a.dateTimeISO).getTime() - new Date(b.dateTimeISO).getTime()
    );

  for (const event of allEvents) {
    const dateKey = dayKeyFromEvent(event);
    if (!byDate.has(dateKey)) byDate.set(dateKey, []);
    byDate.get(dateKey)!.push(event);

    if (!byCurrency.has(event.currency)) byCurrency.set(event.currency, []);
    byCurrency.get(event.currency)!.push(event);

    const impact = normalizeImpact(event.impact);
    if (!byImpact.has(impact)) byImpact.set(impact, []);
    byImpact.get(impact)!.push(event);
  }

  const currencies = Array.from(byCurrency.keys()).sort();
  let dateRange: { start: Date; end: Date } | null = null;
  if (allEvents.length > 0) {
    dateRange = {
      start: new Date(allEvents[0]!.dateTimeISO),
      end: new Date(allEvents[allEvents.length - 1]!.dateTimeISO),
    };
  }

  return { byDate, byCurrency, byImpact, allEvents, currencies, dateRange };
}

function removeIdsFromMap(byId: Map<string, EconomicEvent>, ids: Set<string>) {
  for (const id of ids) byId.delete(id);
}

function collectIdsForDay(byId: Map<string, EconomicEvent>, dayKey: string) {
  const ids = new Set<string>();
  for (const [id, event] of byId) {
    if (dayKeyFromEvent(event) === dayKey) ids.add(id);
  }
  return ids;
}

function collectIdsForMonth(byId: Map<string, EconomicEvent>, monthKey: string) {
  const ids = new Set<string>();
  for (const [id, event] of byId) {
    if (dayKeyFromEvent(event).startsWith(monthKey)) ids.add(id);
  }
  return ids;
}

function collectIdsForYears(byId: Map<string, EconomicEvent>, years: number[]) {
  const yearSet = new Set(years);
  const ids = new Set<string>();
  for (const [id, event] of byId) {
    const y = Number.parseInt(dayKeyFromEvent(event).slice(0, 4), 10);
    if (yearSet.has(y)) ids.add(id);
  }
  return ids;
}

function toStoredEvents(byId: Map<string, EconomicEvent>): StoredEconomicEvent[] {
  return Array.from(byId.values())
    .filter((e) => (e.sequence ?? 0) > 0 && !e.isDeleted)
    .map((e) => ({
      ...e,
      id: e.id ?? eventMapKey(e),
      sequence: e.sequence ?? 0,
    }));
}

export function mapWireEconomicEvent(
  raw: Record<string, unknown>
): EconomicEvent {
  const impactRaw = String(raw.impact ?? raw.Impact ?? "Low");
  return {
    id: String(raw.id ?? raw.Id ?? ""),
    sequence: Number(raw.sequence ?? raw.Sequence ?? 0),
    timeLabel: String(raw.timeLabel ?? raw.TimeLabel ?? ""),
    impact: normalizeImpact(impactRaw),
    actual: String(raw.actual ?? raw.Actual ?? ""),
    forecast: String(raw.forecast ?? raw.Forecast ?? ""),
    previous: String(raw.previous ?? raw.Previous ?? ""),
    name: String(raw.name ?? raw.Name ?? ""),
    currency: String(raw.currency ?? raw.Currency ?? ""),
    date: String(raw.date ?? raw.Date ?? ""),
    dateTimeISO: String(raw.dateTimeISO ?? raw.DateTimeISO ?? ""),
    isDeleted: Boolean(raw.isDeleted ?? raw.IsDeleted ?? false),
  };
}

export const useEconomicCalendarStore = create<EconomicCalendarStore>(
  (set, get) => {
    const getEventsByDate = (date: string) => get().byDate.get(date) || [];
    const getEventsByCurrency = (currency: string) =>
      get().byCurrency.get(currency) || [];
    const getEventsByImpact = (impact: "Low" | "Medium" | "High") =>
      get().byImpact.get(impact) || [];
    const isComplete = () => get().loadingStatus === "complete";

    const applyEventsToMap = (
      byId: Map<string, EconomicEvent>,
      events: EconomicEvent[]
    ) => {
      for (const event of events) {
        const key = eventMapKey(event);
        if (event.isDeleted) {
          byId.delete(key);
          continue;
        }
        byId.set(key, { ...event, id: event.id || key });
      }
    };

    // ── Live-event coalescing ──
    // Live actual/forecast prints arrive one at a time and can burst several per second. Rebuilding the
    // 34k-event indexes (and re-rendering the calendar) per print made the panel blink. Instead we buffer
    // prints, apply them to byId immediately (cheap), and flush ONE index rebuild + render at most every
    // ~1.5s (trailing) — a background-updated view that refreshes every couple seconds without blinking.
    const LIVE_FLUSH_MS = 1500;
    let liveBuffer: EconomicEvent[] = [];
    let liveFlushTimer: ReturnType<typeof setTimeout> | null = null;

    const flushLiveBuffer = () => {
      liveFlushTimer = null;
      if (liveBuffer.length === 0) return;
      const batch = liveBuffer;
      liveBuffer = [];
      set((state) => {
        const newById = new Map(state.byId);
        applyEventsToMap(newById, batch);
        const indexes = rebuildIndexes(newById);
        return {
          ...state,
          byId: newById,
          ...indexes,
          loadingStatus: "complete",
          syncLoaded: true,
        };
      });
    };

    return {
      ...initialState,

      loadFromStorage: async () => {
        // Legacy: events used to live in a single localStorage blob (quota-bound). Drop it — events
        // now live in IndexedDB and re-sync repopulates. Month versions stay in localStorage.
        try {
          localStorage.removeItem(ECONOMIC_CALENDAR_STORAGE_KEYS.SYNC);
        } catch {
          /* ignore */
        }

        const stored = await loadEconomicCalendarEventsIdb();
        const monthVersions = loadMonthVersions();
        const years = getEconomicCalendarSyncYears();

        if (stored.length === 0) {
          set({
            monthVersions,
            calendarSyncTargetYears: years,
            syncLoaded: false,
          });
          return;
        }

        const byId = new Map<string, EconomicEvent>();
        applyEventsToMap(byId, stored);
        const indexes = rebuildIndexes(byId);
        set({
          byId,
          ...indexes,
          monthVersions,
          syncLoaded: true,
          loadingStatus: "complete",
          calendarSyncTargetYears: years,
          loadedCalendarYears: [...years],
        });
      },

      buildSyncRequest: (): VybeSyncEconomicCalendarRequest => {
        const state = get();
        const years = [...getEconomicCalendarSyncYears()];
        const monthKeys = getEconomicCalendarSyncMonthKeys();
        const versions =
          Object.keys(state.monthVersions).length > 0
            ? state.monthVersions
            : loadMonthVersions();

        return {
          calendarYears: years,
          monthVersions: monthKeys.map((monthKey) => ({
            monthKey,
            versionHash: versions[monthKey] ?? "",
          })),
        };
      },

      beginCalendarSync: (years: readonly number[], options) => {
        const calendarSyncTargetYears = [...new Set(years)].sort(
          (a, b) => a - b
        );
        const checking = options?.checking === true;
        set((state) => ({
          calendarSyncTargetYears,
          loadedCalendarYears: checking ? state.loadedCalendarYears : [],
          loadingStatus: checking
            ? state.loadingStatus === "complete"
              ? "complete"
              : calendarSyncTargetYears.length > 0
                ? "loading"
                : "idle"
            : calendarSyncTargetYears.length > 0
              ? "loading"
              : "idle",
          errorMessage: null,
        }));
      },

      processYearData: (year: number, data: EconomicEvent[]) => {
        set((state) => {
          if (state.loadingStatus === "error") return state;

          const newById = new Map(state.byId);
          applyEventsToMap(newById, data);

          const mergedLoaded = state.loadedCalendarYears.includes(year)
            ? state.loadedCalendarYears
            : [...state.loadedCalendarYears, year].sort((a, b) => a - b);

          const target = state.calendarSyncTargetYears;
          const allSynced =
            target.length > 0 &&
            target.every((y) => mergedLoaded.includes(y));

          const indexes = rebuildIndexes(newById);
          const nextLoadingStatus = allSynced
            ? "complete"
            : state.loadingStatus === "idle"
              ? "loading"
              : state.loadingStatus;

          return {
            ...state,
            byId: newById,
            ...indexes,
            loadedCalendarYears: mergedLoaded,
            loadingStatus: nextLoadingStatus,
            syncLoaded: allSynced ? true : state.syncLoaded,
          };
        });
      },

      applySyncBatch: (
        batch,
        isLastBatch,
        replaceScope,
        scopeKey,
        batchIndex = 0,
        serverMonthVersions = []
      ) => {
        set((state) => {
          let newById = new Map(state.byId);
          const shouldReplaceScope = batchIndex === 0;

          if (shouldReplaceScope && replaceScope === "day" && scopeKey) {
            removeIdsFromMap(newById, collectIdsForDay(newById, scopeKey));
          } else if (shouldReplaceScope && replaceScope === "month" && scopeKey) {
            removeIdsFromMap(newById, collectIdsForMonth(newById, scopeKey));
          } else if (shouldReplaceScope && replaceScope === "years" && scopeKey) {
            const years = scopeKey
              .split(",")
              .map((y) => Number.parseInt(y.trim(), 10))
              .filter((y) => !Number.isNaN(y));
            if (years.length > 0)
              removeIdsFromMap(newById, collectIdsForYears(newById, years));
          }

          applyEventsToMap(newById, batch);

          const years = state.calendarSyncTargetYears.length
            ? state.calendarSyncTargetYears
            : getEconomicCalendarSyncYears();

          let monthVersions = state.monthVersions;
          if (serverMonthVersions.length > 0) {
            monthVersions = mergeMonthVersions(
              monthVersions,
              serverMonthVersions.map((v) => ({
                monthKey: v.monthKey,
                versionHash: v.versionHash,
              }))
            );
          }

          const patch: Partial<EconomicCalendarState> = {
            byId: newById,
            monthVersions,
            loadingStatus: isLastBatch ? "complete" : "loading",
            syncLoaded: isLastBatch ? true : state.syncLoaded,
            calendarSyncTargetYears: years,
            loadedCalendarYears: isLastBatch ? [...years] : state.loadedCalendarYears,
            errorMessage: null,
          };

          if (isLastBatch) {
            const indexes = rebuildIndexes(newById);
            Object.assign(patch, indexes);
            void saveEconomicCalendarEventsIdb(toStoredEvents(newById));
            saveMonthVersions(monthVersions);
          }

          return { ...state, ...patch };
        });
      },

      applyLiveEvent: (event) => {
        // Invalidate the month's version hash so the next sync re-pulls it, but DON'T rebuild/render
        // per event — buffer and flush on a timer (see flushLiveBuffer) to avoid the blink.
        const monthKey = monthKeyFromEventDate(event.date);
        const state = get();
        if (state.monthVersions[monthKey]) {
          const nextVersions = { ...state.monthVersions };
          delete nextVersions[monthKey];
          set({ monthVersions: nextVersions });
        }
        liveBuffer.push(event);
        if (liveFlushTimer === null) {
          liveFlushTimer = setTimeout(flushLiveBuffer, LIVE_FLUSH_MS);
        }
      },

      setError: (message: string) =>
        set((state) => ({
          ...state,
          loadingStatus: "error",
          errorMessage: message,
        })),

      reset: () => set(initialState),

      getEventsByDate,
      getEventsByCurrency,
      getEventsByImpact,
      isComplete,
    };
  }
);
